/**
 * A booking, from finding a driver to the receipt: live status, the driver and their car,
 * the map and ETA, cancelling, calling the firm, and a tip once it's done.
 */

import { errorMessage, get, newIdempotencyKey, post } from "@taxi/mobile-web/api";
import { dateParts, longWhen, minutes, money, time } from "@taxi/mobile-web/format";
import { useInterval, useResource } from "@taxi/mobile-web/hooks";
import {
  BagIcon,
  BellIcon,
  BriefcaseIcon,
  CardIcon,
  CashIcon,
  CheckIcon,
  ClockIcon,
  PhoneIcon,
  ReceiptIcon,
  RepeatIcon,
  UsersIcon,
} from "@taxi/mobile-web/icons";
import { type MapDriver, MapView } from "@taxi/mobile-web/map";
import { enablePush, pushState, pushSupport } from "@taxi/mobile-web/push";
import { useRealtime } from "@taxi/mobile-web/realtime";
import { navigate } from "@taxi/mobile-web/router";
import {
  Avatar,
  Button,
  Card,
  Loading,
  Notice,
  Plate,
  Sheet,
  Steps,
  TopBar,
  useToast,
} from "@taxi/mobile-web/ui";
import { useCallback, useEffect, useRef, useState } from "react";
import { PaymentForm, type PaymentFormHandle } from "../components/payment-form";
import { placeTitle, useDraft } from "../lib/draft";
import { useSession } from "../lib/session";
import type { Booking, Place, TipResult } from "../lib/types";

const LIVE_STATUSES = ["dispatching", "offered", "accepted", "arrived", "on_board"];

const asPlace = (p: Booking["pickup"]): Place => ({
  address: p.address,
  postcode: p.postcode,
  lat: p.lat,
  lng: p.lng,
  name: p.name,
});

function headline(b: Booking): { title: string; detail: string } {
  const driver = b.driver?.name.split(" ")[0] ?? "Your driver";
  const car = b.driver?.vehicle;
  switch (b.status) {
    case "pending_payment":
      return { title: "Waiting for payment", detail: "Finish paying to confirm your booking." };
    case "scheduled":
      return {
        title: `You're booked`,
        detail: "We'll find you a driver nearer the time and let you know when they're on the way.",
      };
    case "dispatching":
    case "offered":
      return { title: "Finding you a driver", detail: "This usually takes a minute or two." };
    case "accepted":
      return {
        title: `${driver} is on the way`,
        detail: `Heading to ${placeTitle(asPlace(b.pickup))}`,
      };
    case "arrived":
      return {
        title: `${driver} is here`,
        detail: car
          ? `Look for a ${car.colour.toLowerCase()} ${car.make} ${car.model}`
          : "Your driver is at the pickup",
      };
    case "on_board":
      return { title: "On your way", detail: `To ${placeTitle(asPlace(b.dropoff))}` };
    case "completed":
      return { title: "You've arrived", detail: "Thanks for travelling with us." };
    case "no_show":
      return {
        title: "Your driver couldn't find you",
        detail: "They waited at the pickup. Call us if you still need a taxi.",
      };
    case "cancelled":
      return { title: "Booking cancelled", detail: "You haven't been charged." };
  }
}

export function BookingScreen({ id }: { id: string }) {
  const { firmPhone } = useSession();
  const booking = useResource(() => get<Booking>(`/customer/bookings/${id}`), [id]);
  const [live, setLive] = useState<MapDriver | null>(null);
  const reload = booking.reload;

  const onEvent = useCallback(
    (event: Parameters<Parameters<typeof useRealtime>[0]>[0]) => {
      if (event.type === "reconnected") void reload();
      if ((event.type === "job.updated" || event.type === "job.created") && event.jobId === id) {
        void reload();
      }
      if (event.type === "driver.location" && event.jobId === id) {
        setLive({ lat: event.lat, lng: event.lng, heading: event.heading });
      }
    },
    [id, reload],
  );
  const b = booking.data;
  const isLive = b ? LIVE_STATUSES.includes(b.status) || b.status === "pending_payment" : false;
  const { connected } = useRealtime(onEvent, !b || isLive || b.status === "scheduled");
  // Without the live connection, check in now and then; with it, refresh the ETA.
  useInterval(
    () => void reload(),
    b && isLive ? (connected ? (b.status === "accepted" ? 30_000 : 60_000) : 10_000) : null,
  );

  if (booking.error && !b) {
    return (
      <div className="booking">
        <TopBar title="Booking" back="/bookings" />
        <div className="screen-body">
          <Notice tone="error">{errorMessage(booking.error)}</Notice>
        </div>
      </div>
    );
  }
  if (!b) return <Loading />;

  const { title, detail } = headline(b);
  const location = live ?? (b.driver?.location ? { ...b.driver.location } : null);
  const showDriverOnMap = ["accepted", "arrived", "on_board"].includes(b.status) ? location : null;
  const finished = ["completed", "no_show", "cancelled"].includes(b.status);
  const stops = b.stops.map(asPlace);
  const step = STEP[b.status];
  const eta = b.status === "accepted" ? b.driver?.etaSeconds : null;

  return (
    <div className={`booking booking-${b.status}${finished ? " booking-finished" : ""}`}>
      <TopBar title={`Booking ${b.number}`} back="/bookings" transparent={!finished} />
      {!finished && (
        <MapView
          className="booking-map"
          pickup={b.status === "on_board" ? null : b.pickup}
          stops={b.status === "on_board" ? stops : []}
          dropoff={b.status === "accepted" || b.status === "arrived" ? null : b.dropoff}
          driver={showDriverOnMap}
          bottomInset={24}
          labels={{
            pickup: b.status === "arrived" ? undefined : placeTitle(asPlace(b.pickup)),
            dropoff: placeTitle(asPlace(b.dropoff)),
            driver: eta ? `${minutes(eta)} away` : b.status === "arrived" ? "Here" : undefined,
          }}
        />
      )}
      <div className={finished ? "screen-body booking-done" : "booking-sheet"}>
        {step !== undefined && <Steps steps={STEPS} current={step} />}

        {b.status === "completed" ? (
          <TripDone booking={b} />
        ) : (
          <div className={`status-block status-${b.status}`}>
            {b.status === "scheduled" && <DateTile at={new Date(b.pickupAt)} />}
            <div className="status-text">
              <h2>{title}</h2>
              <p>
                {b.status === "scheduled" ? `${longWhen(new Date(b.pickupAt))}. ${detail}` : detail}
              </p>
            </div>
            {eta ? (
              <div className="eta-tile" role="timer">
                <strong>{Math.max(1, Math.round(eta / 60))}</strong>
                <span>min</span>
              </div>
            ) : null}
            {(b.status === "dispatching" || b.status === "offered") && (
              <span className="radar" aria-hidden="true">
                <i />
                <i />
                <i />
              </span>
            )}
          </div>
        )}

        {b.payment?.status === "failed" && !finished && (
          <Notice
            tone="error"
            action={
              <Button block={false} onClick={() => navigate(`/bookings/${b.id}/pay`)}>
                Pay again
              </Button>
            }
          >
            We couldn't take payment{b.payment.failureReason ? `: ${b.payment.failureReason}` : "."}
          </Notice>
        )}
        {b.status === "pending_payment" && (
          <Button onClick={() => navigate(`/bookings/${b.id}/pay`)}>Finish paying</Button>
        )}

        {b.driver && ["accepted", "arrived", "on_board"].includes(b.status) && (
          <DriverCard booking={b} />
        )}

        {!finished && b.status !== "pending_payment" && <PushPrompt />}

        {b.status === "completed" && <AfterTrip booking={b} onChange={reload} />}

        <TripDetails booking={b} />

        <div className="booking-actions">
          {finished && <BookAgain booking={b} />}
          <a className="btn btn-secondary btn-block" href={`tel:${firmPhone}`}>
            <PhoneIcon size={18} /> Call us
          </a>
          {b.canCancel && <CancelButton booking={b} onCancelled={reload} />}
        </div>
      </div>
    </div>
  );
}

const STEPS = ["Booked", "On the way", "Arrived", "In the car"];
const STEP: Partial<Record<Booking["status"], number>> = {
  dispatching: 0,
  offered: 0,
  accepted: 1,
  arrived: 2,
  on_board: 3,
};

/** The pickup day, like a page from a calendar. */
function DateTile({ at }: { at: Date }) {
  const { weekday, day, month } = dateParts(at);
  return (
    <span className="date-tile" aria-hidden="true">
      <span>{weekday}</span>
      <strong>{day}</strong>
      <span>{month}</span>
    </span>
  );
}

/** The end of the trip: the fare and how it was paid, and the driver. */
function TripDone({ booking: b }: { booking: Booking }) {
  const { firm } = useSession();
  const fare = b.finalFarePence ?? b.pricePence;
  const paid =
    b.paymentMethod === "card"
      ? b.payment?.cardLast4
        ? `Paid with ${cardName(b.payment.cardBrand)} •••• ${b.payment.cardLast4}`
        : "Paid by card"
      : b.paymentMethod === "cash"
        ? "Paid in cash to the driver"
        : "On your account's invoice";
  return (
    <section className="trip-done">
      <span className="trip-done-icon" aria-hidden="true">
        <CheckIcon size={30} strokeWidth={2.6} />
      </span>
      <h2>You've arrived</h2>
      <p>
        At {placeTitle(asPlace(b.dropoff))}. Thanks for travelling with {firm.name}.
      </p>
      <strong className="trip-done-fare">{money(fare)}</strong>
      <span className="pill">
        {b.paymentMethod === "card" ? (
          <CardIcon size={15} />
        ) : b.paymentMethod === "cash" ? (
          <CashIcon size={15} />
        ) : (
          <BriefcaseIcon size={15} />
        )}{" "}
        {paid}
      </span>
      {b.driver && (
        <span className="trip-done-driver">
          <Avatar name={b.driver.name} photoUrl={b.driver.photoUrl} size={28} />
          Your driver was {b.driver.name}
        </span>
      )}
    </section>
  );
}

const cardName = (brand: string | null) =>
  brand ? brand.charAt(0).toUpperCase() + brand.slice(1) : "Card";

function DriverCard({ booking }: { booking: Booking }) {
  const driver = booking.driver!;
  const car = driver.vehicle;
  return (
    <Card className="driver-card">
      <Avatar name={driver.name} photoUrl={driver.photoUrl} size={56} />
      <div className="driver-card-text">
        <span className="overline">Your driver</span>
        <strong>{driver.name}</strong>
        {car && (
          <span>
            {car.colour} {car.make} {car.model}
          </span>
        )}
      </div>
      {car && <Plate registration={car.registration} />}
    </Card>
  );
}

function TripDetails({ booking: b }: { booking: Booking }) {
  const payment =
    b.paymentMethod === "card"
      ? b.payment?.cardLast4
        ? `${b.payment.cardBrand ? b.payment.cardBrand.charAt(0).toUpperCase() + b.payment.cardBrand.slice(1) : "Card"} •••• ${b.payment.cardLast4}`
        : "Card"
      : b.paymentMethod === "cash"
        ? "Cash to the driver"
        : "Pay on Account";
  const fare = b.finalFarePence ?? b.pricePence;
  return (
    <Card className="trip-details">
      <ol className="route-list">
        <li className="pickup">
          <strong>{placeTitle(asPlace(b.pickup))}</strong>
          <span>{b.pickup.address}</span>
        </li>
        {b.stops.map((stop) => (
          <li key={`${stop.lat},${stop.lng}`} className="stop">
            <strong>{placeTitle(asPlace(stop))}</strong>
          </li>
        ))}
        <li className="dropoff">
          <strong>{placeTitle(asPlace(b.dropoff))}</strong>
          <span>{b.dropoff.address}</span>
        </li>
      </ol>
      <dl className="facts">
        <div>
          <dt>
            <ClockIcon size={16} /> Pickup
          </dt>
          <dd>
            {b.isAsap
              ? `Booked for now (${time(new Date(b.pickupAt))})`
              : longWhen(new Date(b.pickupAt))}
          </dd>
        </div>
        <div>
          <dt>
            <UsersIcon size={16} /> {b.vehicleType.name}
          </dt>
          <dd>
            {b.passengers} {b.passengers === 1 ? "passenger" : "passengers"}
            {b.luggage > 0 && (
              <>
                {" · "}
                <BagIcon size={14} /> {b.luggage}
              </>
            )}
          </dd>
        </div>
        {b.passengerName && (
          <div>
            <dt>Passenger</dt>
            <dd>{b.passengerName}</dd>
          </div>
        )}
        {b.notes && (
          <div>
            <dt>Note for the driver</dt>
            <dd>{b.notes}</dd>
          </div>
        )}
        <div>
          <dt>
            {b.paymentMethod === "card" ? (
              <CardIcon size={16} />
            ) : b.paymentMethod === "cash" ? (
              <CashIcon size={16} />
            ) : (
              <BriefcaseIcon size={16} />
            )}{" "}
            {payment}
          </dt>
          <dd className="fare">
            {money(fare)}
            {b.isEstimate && <small> estimate</small>}
          </dd>
        </div>
        {b.tip && b.tip.status === "succeeded" && (
          <div>
            <dt>Tip</dt>
            <dd>{money(b.tip.amountPence)}</dd>
          </div>
        )}
      </dl>
    </Card>
  );
}

function CancelButton({
  booking,
  onCancelled,
}: {
  booking: Booking;
  onCancelled: () => Promise<void>;
}) {
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  return (
    <>
      <Button variant="ghost" className="danger-text" onClick={() => setOpen(true)}>
        Cancel booking
      </Button>
      <Sheet open={open} title="Cancel this booking?" onClose={() => setOpen(false)}>
        <p>
          It's free to cancel before you're picked up.
          {booking.driver && booking.status !== "scheduled"
            ? ` We'll let ${booking.driver.name} know.`
            : ""}
        </p>
        {error && <Notice tone="error">{error}</Notice>}
        <Button
          variant="danger"
          loading={busy}
          onClick={async () => {
            setBusy(true);
            try {
              await post(`/customer/bookings/${booking.id}/cancel`, {});
              setOpen(false);
              toast("Booking cancelled");
              await onCancelled();
            } catch (err) {
              setError(errorMessage(err));
            } finally {
              setBusy(false);
            }
          }}
        >
          Yes, cancel it
        </Button>
        <Button variant="ghost" onClick={() => setOpen(false)}>
          Keep my booking
        </Button>
      </Sheet>
    </>
  );
}

function BookAgain({ booking }: { booking: Booking }) {
  const { reset } = useDraft();
  return (
    <Button
      variant="secondary"
      onClick={() => {
        reset({
          pickup: asPlace(booking.pickup),
          dropoff: asPlace(booking.dropoff),
          stops: booking.stops.map(asPlace),
          passengers: booking.passengers,
        });
        navigate("/book/quotes");
      }}
    >
      <RepeatIcon size={18} /> Book this trip again
    </Button>
  );
}

/** Asks to turn on notifications while a trip is coming up, if they're not on yet. */
function PushPrompt() {
  const { firm } = useSession();
  const toast = useToast();
  const support = pushSupport();
  const [state, setState] = useState<"unknown" | "on" | "off" | "blocked" | "dismissed">(() => {
    try {
      return localStorage.getItem("pushPromptDismissed") ? "dismissed" : "unknown";
    } catch {
      return "unknown";
    }
  });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (state !== "unknown" || support !== "supported") return;
    void pushState().then((s) => setState(s === "on" ? "on" : s === "blocked" ? "blocked" : "off"));
  }, [state, support]);
  if (!firm.webPush.publicKey || state === "on" || state === "blocked" || state === "dismissed")
    return null;
  if (support === "unsupported") return null;
  const dismiss = () => {
    try {
      localStorage.setItem("pushPromptDismissed", "1");
    } catch {
      // Shown again next time.
    }
    setState("dismissed");
  };
  return (
    <Card className="push-prompt">
      <BellIcon />
      <div>
        <strong>Know when your driver's on the way</strong>
        <span>
          {support === "add-to-home-screen"
            ? "Add this app to your Home Screen (Share, then Add to Home Screen) to get notifications."
            : "Get a notification when they're on the way and when they arrive."}
        </span>
        <div className="push-prompt-actions">
          {support === "supported" && (
            <Button
              block={false}
              loading={busy}
              onClick={async () => {
                setBusy(true);
                try {
                  const result = await enablePush("customer", firm.webPush.publicKey!);
                  setState(result);
                  if (result === "on") toast("Notifications are on");
                } catch (err) {
                  toast(errorMessage(err), "error");
                } finally {
                  setBusy(false);
                }
              }}
            >
              Turn on
            </Button>
          )}
          <Button block={false} variant="ghost" onClick={dismiss}>
            Not now
          </Button>
        </div>
      </div>
    </Card>
  );
}

/** After the trip: the fare, a tip for card trips, and the receipt. */
function AfterTrip({ booking, onChange }: { booking: Booking; onChange: () => Promise<void> }) {
  return (
    <>
      {booking.canTip && <TipCard booking={booking} onTipped={onChange} />}
      {booking.tip?.status === "succeeded" && (
        <Notice tone="success">
          You tipped {money(booking.tip.amountPence)}. Thank you! All of it goes to{" "}
          {booking.driver?.name ?? "your driver"}.
        </Notice>
      )}
      {booking.hasReceipt && (
        <Button variant="secondary" onClick={() => navigate(`/bookings/${booking.id}/receipt`)}>
          <ReceiptIcon size={18} /> Receipt
        </Button>
      )}
    </>
  );
}

function TipCard({ booking, onTipped }: { booking: Booking; onTipped: () => Promise<void> }) {
  const { firm } = useSession();
  const toast = useToast();
  const presets = firm.payments.tipPresetsPence;
  const [amount, setAmount] = useState<number | null>(null);
  const [custom, setCustom] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [needsCard, setNeedsCard] = useState<TipResult | null>(null);
  const [ready, setReady] = useState(false);
  const key = useRef(newIdempotencyKey());
  const form = useRef<PaymentFormHandle>(null);
  const chosen = custom ? Math.round(Number.parseFloat(custom) * 100) : amount;
  const valid = chosen !== null && Number.isFinite(chosen) && chosen >= 50 && chosen <= 100_00;

  async function finish(tipId: string) {
    const tip = await post<{ status: string; failureReason: string | null }>(
      `/customer/tips/${tipId}/confirm`,
    );
    if (tip.status !== "succeeded")
      throw new Error(tip.failureReason ?? "The tip didn't go through");
    toast(
      `Thank you! ${money(chosen ?? 0)} is on its way to ${booking.driver?.name ?? "your driver"}.`,
    );
    await onTipped();
  }

  async function tip() {
    if (!valid || chosen === null) return;
    setBusy(true);
    setError(null);
    try {
      const result = await post<TipResult>(
        `/customer/bookings/${booking.id}/tip`,
        { amountPence: chosen },
        { idempotencyKey: key.current },
      );
      if (result.tip.status === "succeeded") {
        toast(
          `Thank you! ${money(chosen)} is on its way to ${booking.driver?.name ?? "your driver"}.`,
        );
        await onTipped();
      } else if (result.paymentSheet) {
        // The bank wants to check with the customer first.
        setNeedsCard(result);
      } else {
        setError(result.tip.failureReason ?? "The tip didn't go through. Try again.");
      }
    } catch (err) {
      setError(errorMessage(err));
      key.current = newIdempotencyKey();
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card className="tip-card">
      <h3>Add a tip for {booking.driver?.name.split(" ")[0] ?? "your driver"}?</h3>
      <p>All of it goes to them, on the card you paid with.</p>
      <div className="tip-options">
        {presets.map((p) => (
          <button
            type="button"
            key={p}
            className={`tip-option${amount === p && !custom ? " active" : ""}`}
            onClick={() => {
              setAmount(p);
              setCustom("");
            }}
          >
            {money(p)}
          </button>
        ))}
        <label className={`tip-option tip-custom${custom ? " active" : ""}`}>
          <span>£</span>
          <input
            inputMode="decimal"
            placeholder="Other"
            aria-label="Another amount, in pounds"
            value={custom}
            onChange={(e) => setCustom(e.target.value.replace(/[^\d.]/g, "").slice(0, 6))}
          />
        </label>
      </div>
      {error && <Notice tone="error">{error}</Notice>}
      <Button disabled={!valid} loading={busy} onClick={tip}>
        {valid && chosen !== null ? `Tip ${money(chosen)}` : "Choose an amount"}
      </Button>
      <Sheet open={Boolean(needsCard)} title="Confirm your tip" onClose={() => setNeedsCard(null)}>
        {needsCard?.paymentSheet && (
          <>
            <p>Your bank wants to check this payment with you.</p>
            <PaymentForm
              ref={form}
              sheet={needsCard.paymentSheet}
              returnUrl={`${window.location.origin}/bookings/${booking.id}`}
              onReady={setReady}
            />
            <Button
              loading={busy}
              disabled={!ready}
              onClick={async () => {
                setBusy(true);
                setError(null);
                const problem = await form.current!.confirm();
                try {
                  if (problem) throw new Error(problem);
                  await finish(needsCard.tip.id);
                  setNeedsCard(null);
                } catch (err) {
                  setError(err instanceof Error ? err.message : errorMessage(err));
                  setNeedsCard(null);
                } finally {
                  setBusy(false);
                }
              }}
            >
              Tip {money(needsCard.tip.amountPence)}
            </Button>
          </>
        )}
      </Sheet>
    </Card>
  );
}
